import { useState, useEffect } from "react";
import axios from "axios";
import { X, Building2, Users } from "lucide-react";
import { API } from "@/utils/constants";
import StatusBadge from "@/components/common/StatusBadge";
import CollapsibleSection from "@/components/common/CollapsibleSection";
import JsonViewer from "@/components/common/JsonViewer";

// Rule Compliance Card Component
const RuleComplianceCard = ({ title, status, matchReason, overComplianceReason, configJson, ruleName }) => {
  // If status is Non-Compliant, don't show Over-Compliant status
  const isNonCompliant = status?.toLowerCase() === 'non-compliant';
  const effectiveStatus = !isNonCompliant && overComplianceReason && overComplianceReason !== 'None' && overComplianceReason.trim() !== '' 
    ? 'Over-Compliant' 
    : status;
  
  // Determine label based on status
  const getReasonLabel = () => {
    if (isNonCompliant) return 'Compliance Reason (Why Non-Compliant)';
    return 'Compliance Reason';
  };
  
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
      {/* Header with Status */}
      <div className="p-4 border-b border-slate-700 bg-slate-800/50">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-white">{title}</h4>
          <StatusBadge status={effectiveStatus} size="large" />
        </div>
        {ruleName && <p className="text-sm text-slate-400 mt-1">{ruleName}</p>}
      </div>
      
      {/* Compliance Reason - Primary information */}
      <div className="p-4 space-y-4">
        {matchReason && (
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isNonCompliant ? 'text-red-400' : 'text-slate-400'}`}>
              {getReasonLabel()}
            </p>
            <p className={`text-sm p-3 rounded-lg border ${
              isNonCompliant 
                ? 'text-red-300 bg-red-950/30 border-red-800/50' 
                : 'text-slate-300 bg-slate-900 border-slate-700'
            }`}>
              {matchReason}
            </p>
          </div>
        )}
        
        {/* Over-Compliance Note - Only show if NOT Non-Compliant */}
        {!isNonCompliant && overComplianceReason && overComplianceReason !== 'None' && overComplianceReason.trim() !== '' && (
          <div>
            <p className="text-xs font-semibold text-amber-400 uppercase tracking-wide mb-2">Over-Compliance Note</p>
            <p className="text-sm text-amber-300 bg-amber-950/30 p-3 rounded-lg border border-amber-800/50">
              {overComplianceReason}
            </p>
          </div>
        )}
        
        {/* Drill-down into Configuration Details */}
        {configJson && (
          <CollapsibleSection title="View Configuration Details">
            <JsonViewer data={configJson} />
          </CollapsibleSection>
        )}
      </div>
    </div>
  );
};

const RuleDetailModal = ({ ruleId, onClose }) => {
  const [rule, setRule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employeeData, setEmployeeData] = useState(null);

  useEffect(() => {
    const fetchRule = async () => {
      try {
        const [ruleResponse, employeeResponse] = await Promise.all([
          axios.get(`${API}/compliance/rules/${ruleId}`),
          axios.get(`${API}/compliance/rules/${ruleId}/employees`)
        ]);
        setRule(ruleResponse.data);
        setEmployeeData(employeeResponse.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    if (ruleId) fetchRule();
  }, [ruleId]);

  if (!ruleId) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div 
        data-testid="rule-detail-modal"
        className="bg-slate-900 rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col border border-slate-700"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 bg-slate-800 border-b border-slate-700">
          <div>
            <h2 className="text-xl font-bold text-white font-['Manrope']">
              {loading ? 'Loading...' : rule?.payrule || 'Rule Details'}
            </h2>
            {rule && (
              <div className="flex items-center gap-4 mt-2">
                <span className="text-sm text-slate-400">
                  <Building2 className="w-4 h-4 inline mr-1" />
                  {rule.state_name}
                </span>
                <span className="text-sm text-slate-500">ID: {rule.attestation_path_id}</span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="close-modal-btn"
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 bg-slate-900/50">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2EC4B6]"></div>
            </div>
          ) : rule ? (
            <div className="space-y-5">
              {/* Overall Status Card - Most Important */}
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-5">
                {(() => {
                  const isOverallNonCompliant = rule.overall_compliance_status?.toLowerCase() === 'non-compliant';
                  const effectiveOverallStatus = !isOverallNonCompliant && rule.overall_over_compliance_reason && rule.overall_over_compliance_reason !== 'None'
                    ? 'Over-Compliant'
                    : rule.overall_compliance_status;
                  
                  return (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-white">Overall Compliance Status</h3>
                        <StatusBadge 
                          status={effectiveOverallStatus} 
                          size="large" 
                        />
                      </div>
                      
                      {/* Overall Compliance Reason */}
                      {rule.overall_compliance_reason && (
                        <div className="mb-4">
                          <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isOverallNonCompliant ? 'text-red-400' : 'text-slate-400'}`}>
                            {isOverallNonCompliant ? 'Why Non-Compliant' : 'Summary'}
                          </p>
                          <p className={`text-sm p-4 rounded-lg border ${
                            isOverallNonCompliant 
                              ? 'text-red-300 bg-red-950/30 border-red-800/50' 
                              : 'text-slate-300 bg-slate-900 border-slate-700'
                          }`}>
                            {rule.overall_compliance_reason}
                          </p>
                        </div>
                      )}
                      
                      {/* Overall Over-Compliance - Only show if NOT Non-Compliant */}
                      {!isOverallNonCompliant && rule.overall_over_compliance_reason && rule.overall_over_compliance_reason !== 'None' && (
                        <div>
                          <p className="text-xs font-semibold text-amber-400 uppercase tracking-wide mb-2">Over-Compliance Notes</p>
                          <p className="text-sm text-amber-300 bg-amber-950/30 p-4 rounded-lg border border-amber-800/50">
                            {rule.overall_over_compliance_reason}
                          </p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              {/* Employee Impact Section */}
              {employeeData && employeeData.total_employees > 0 && (
                <div className="bg-gradient-to-r from-slate-800 to-slate-800/50 border border-slate-700 rounded-lg p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-[#2EC4B6]" />
                      <h3 className="text-lg font-bold text-white">Employee Impact</h3>
                    </div>
                    <span className="text-2xl font-bold text-[#2EC4B6]">{employeeData.total_employees}</span>
                  </div>
                  
                  {/* Employee counts by state */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {employeeData.by_state.map(({ state, count }) => (
                      <div key={state} className="bg-slate-900 rounded-lg p-3 border border-slate-700">
                        <p className="text-xs text-slate-400 uppercase tracking-wide">{state}</p>
                        <p className="text-xl font-bold text-white">{count}</p>
                        <p className="text-xs text-slate-500">employees</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* State Law Reference - Collapsible */}
              {rule.law_json && (
                <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
                  <CollapsibleSection title={`State Law Reference (${rule.state_name})`} defaultOpen={false}>
                    <div className="space-y-3">
                      {rule.law_json.Minimum_Wage_Amount && (
                        <div className="flex gap-2">
                          <span className="font-medium text-slate-400 min-w-[140px]">Minimum Wage:</span>
                          <span className="text-slate-300">${rule.law_json.Minimum_Wage_Amount}</span>
                        </div>
                      )}
                      {rule.law_json.Daily_Overtime && (
                        <div>
                          <p className="font-medium text-slate-400 mb-1">Daily Overtime:</p>
                          <p className="text-slate-300 text-sm bg-slate-900 p-2 rounded">{rule.law_json.Daily_Overtime}</p>
                        </div>
                      )}
                      {rule.law_json.Weekly_Overtime && (
                        <div>
                          <p className="font-medium text-slate-400 mb-1">Weekly Overtime:</p>
                          <p className="text-slate-300 text-sm bg-slate-900 p-2 rounded">{rule.law_json.Weekly_Overtime}</p>
                        </div>
                      )}
                      {rule.law_json.Meal_Break_Law && (
                        <div>
                          <p className="font-medium text-slate-400 mb-1">Meal Break Law:</p>
                          <p className="text-slate-300 text-sm bg-slate-900 p-2 rounded">{rule.law_json.Meal_Break_Law}</p>
                        </div>
                      )}
                      {rule.law_json.Rest_Break_Law && (
                        <div>
                          <p className="font-medium text-slate-400 mb-1">Rest Break Law:</p>
                          <p className="text-slate-300 text-sm bg-slate-900 p-2 rounded">{rule.law_json.Rest_Break_Law}</p>
                        </div>
                      )}
                    </div>
                  </CollapsibleSection>
                </div>
              )}

              {/* Rule Components Grid */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Pay Rule */}
                <RuleComplianceCard
                  title="Pay Rule"
                  status={rule.payrule_compliance_status}
                  matchReason={rule.payrule_compliance_reason}
                  overComplianceReason={rule.payrule_over_compliance_reason}
                  configJson={rule.payrule_json}
                  ruleName={rule.payrule}
                />

                {/* Work Rule */}
                <RuleComplianceCard
                  title="Work Rule"
                  status={rule.workrule_compliance_status}
                  matchReason={rule.workrule_compliance_reason || rule.workrule_match_reason}
                  overComplianceReason={rule.workrule_overcompliance_compliance_reason}
                  configJson={rule.workrule_json}
                  ruleName={rule.workrule}
                />

                {/* Overtime Rule */}
                <RuleComplianceCard
                  title="Overtime Rule"
                  status={rule.overtimerule_compliance_status}
                  matchReason={rule.overtimerule_compliance_reason || rule.overtime_match_reason}
                  overComplianceReason={rule.overtimerule_over_compliance_reason}
                  configJson={rule.overtimerule_json}
                  ruleName={rule.overtimerule}
                />

                {/* Combination Rule */}
                <RuleComplianceCard
                  title="Combination Rule"
                  status={rule.combinationrule_status}
                  matchReason={rule.combinationrule_reason || rule.combinationrule_match_reason}
                  overComplianceReason={rule.combinationrule_over_compliance_reason}
                  configJson={rule.combinationrule_json}
                  ruleName={rule.combinationrulename}
                />

                {/* Pay Code Distribution */}
                <RuleComplianceCard
                  title="Pay Code Distribution"
                  status={rule.paycodedistribution_status}
                  matchReason={rule.paycodedistribution_reason || rule.paycodedistribution_match_reason}
                  overComplianceReason={rule.paycodedistribution_over_compliance_reason}
                  configJson={rule.paycodedistribution_json}
                  ruleName={rule.paycodedistributionname}
                />
              </div>
            </div>
          ) : (
            <p className="text-slate-400">No data available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RuleDetailModal;
